import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EntityType } from "@repo/types/enums";
import type { AddFileAssetsViewModel } from "../../types/viewModels/files/AddFileAssetsViewModel";
import type FileAssetDTO from "../../types/dtos/fileAssets/FileAssetDTO";
import type { RecordId } from "@repo/types/ui/tables";
import { objectToFormData } from "../../helpers/objectToFormData";
import { useApiAccess } from "@repo/hooks/contexts";
import type { ExecutionResponse } from "@repo/types/responses";
import { fileAssetKeys } from "@repo/types/queries";

export const getUploadDuration = (files: File[]) => {
  const fileCount = files.length;

  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);

  const totalMB = totalBytes / (1024 * 1024);

  const duration = fileCount * 250 + totalMB * 180;

  return Math.min(Math.max(duration, 800), 6000);
};

export const useAddMultipleFileAssets = () => {
  const { apiClient } = useApiAccess();
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<number | null>(null);

  const startFakeProgress = (files: File[]) => {
    const duration = getUploadDuration(files);
    const interval = 30;
    const max = 90;

    let current = 0;
    const step = max / (duration / interval);

    timerRef.current = window.setInterval(() => {
      current += step;

      if (current >= max) {
        current = max;
        clearInterval(timerRef.current!);
      }

      setProgress(Math.round(current));
    }, interval);
  };

  const stopFakeProgress = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setProgress(100);
  };

  const mutation = useMutation({
    mutationFn: async (fileAssets: AddFileAssetsViewModel) => {
      startFakeProgress(fileAssets.Files);

      const formData = objectToFormData(fileAssets);

      const res = await apiClient.post<ExecutionResponse>(
        "/FileAsset/AddMultiple",
        formData,
      );

      return res.data;
    },

    onSuccess: (_, fileAssets) => {
      fileAssetKeys.invalidateListByRelationId(queryClient, {
        entityId: fileAssets.EntityId,
        entityType: fileAssets.EntityType,
      });

      stopFakeProgress();
    },

    onError: () => {
      if (timerRef.current) clearInterval(timerRef.current);
      setProgress(0);
    },
  });

  return {
    ...mutation,
    progress,
  };
};

export const useGetFileAssetsByEntityId = (
  entityId: RecordId,
  entityType: EntityType,
) => {
  const { apiClient } = useApiAccess();

  return useQuery<FileAssetDTO[]>({
    queryKey: fileAssetKeys.listByRelationId({ entityId, entityType }),
    queryFn: async () => {
      const response = await apiClient.get<FileAssetDTO[]>(
        `/FileAsset/GetByEntityId?entityId=${entityId}&entityType=${entityType}`,
      );
      return response.data;
    },
  });
};

export const useDeleteFileAsset = (
  recordId: RecordId,
  entityType: EntityType,
) => {
  const { apiClient } = useApiAccess();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (assetId: number) => {
      await apiClient.post<ExecutionResponse>(
        `/FileAsset/Delete`,
        JSON.stringify(assetId),
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
    },
    onSuccess: () => {
      // invalidate all queries that could be affected by this change
      fileAssetKeys.invalidateListByRelationId(queryClient, {
        entityId: recordId,
        entityType: entityType,
      });
    },
  });
};

export const useDownloadFileAsset = () => {
  const { apiClient } = useApiAccess();

  return useMutation({
    mutationFn: async (assetId: number) => {
      const res = await apiClient.get("/FileAsset/Download", {
        params: { assetId },
        responseType: "blob",
      });

      return res;
    },
    onSuccess: (res) => {
      const disposition = res.headers["content-disposition"];

      const fileName = getFileNameFromDisposition(disposition);

      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    },
  });
};

const getFileNameFromDisposition = (disposition?: string) => {
  if (!disposition) return "download";

  // Prefer filename*=UTF-8''
  const utf8Match = disposition.match(/filename\*\=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  // Fallback filename=
  const normalMatch = disposition.match(/filename="?([^"]+)"?/i);
  if (normalMatch?.[1]) {
    return normalMatch[1];
  }

  return "download";
};
